import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, BellOff, CheckCircle, XCircle, Clock, AlertCircle, Download } from 'lucide-react';
import { DateTimeDisplay, getTimezoneAbbr, useTimezone } from '@t-one-internal/design-system';
import { customReportsApi } from '../../api/customReportsApi';
import { getReportDownloadUrl } from '../../api/downloadApi';
import type { GeneratedReportDetail } from '../../components/reports/custom-builder/types';
import { DataGrid } from '@t-one-internal/design-system/lib/components/ui/data-grid';
import type { ColumnDef } from '@tanstack/react-table';

const ReportViewerPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { timezone } = useTimezone();
    const tzAbbr = getTimezoneAbbr(timezone);
    const [report, setReport] = useState<GeneratedReportDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [unsubscribing, setUnsubscribing] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const loadReport = useCallback(async () => {
        if (!id) return;

        try {
            setLoading(true);
            setError(null);
            const foundReport = await customReportsApi.getGeneratedReportById(id);
            setReport(foundReport);
        } catch (err) {
            console.error('Failed to load report:', err);
            setError('Failed to load report.');
            setTimeout(() => navigate('/analytics/reports/configure'), 2000);
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    // Extract columns from report data
    const columnsList = useMemo(() => {
        // Columns are now directly in report.data.columns
        return report?.data?.columns || [];
    }, [report?.data?.columns]);

    // Extract rows from report data
    const rows = useMemo(() => {
        // Data rows are now directly in report.data.data
        return report?.data?.data || [];
    }, [report?.data?.data]);

    // Generate columns for DataGrid
    const gridColumns = useMemo<ColumnDef<Record<string, unknown>>[]>(() => {
        return columnsList.map((col: any) => {
            const isString = typeof col === 'string';
            const colId = isString ? col : col.name;
            const colLabel = isString ? col.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : col.label;

            return {
                accessorKey: colId,
                header: colLabel,
                enableColumnFilter: false,
                enableSorting: true,
                size: colId === 'email' ? 250 : 150,
                meta: {
                    label: colLabel,
                    variant: 'text'
                },
                cell: ({ getValue }: { getValue: () => unknown }) => {
                    const val = getValue();
                    if (val === null || val === undefined) {
                        return <span className="text-sm text-gray-700">-</span>;
                    }
                    // Truncate long decimal strings (like "4.5000000000000000") to 4 chars
                    const strVal = String(val);
                    // Normalize casing for status-like columns (e.g., cycle_status)
                    const isStatusCol = colId.toLowerCase().includes('status');
                    let displayVal = strVal;
                    if (isStatusCol && strVal === strVal.toUpperCase() && strVal.length > 1) {
                        displayVal = strVal.charAt(0).toUpperCase() + strVal.slice(1).toLowerCase();
                    }
                    displayVal = displayVal.match(/^\d+\.\d{10,}$/) ? displayVal.slice(0, 4) : displayVal;
                    // Email column gets truncation with ellipsis
                    if (colId === 'email') {
                        return <span className="text-sm text-gray-700 block truncate max-w-[230px]" title={strVal}>{displayVal}</span>;
                    }
                    return <span className="text-sm text-gray-700">{displayVal}</span>;
                }
            };
        });
    }, [columnsList]);

    const handleUnsubscribe = async () => {
        if (!report) return;
        if (!confirm('Are you sure you want to unsubscribe from this report? You will no longer receive email notifications.')) return;

        try {
            setUnsubscribing(true);
            await customReportsApi.unsubscribeFromReport(report.custom_report_id.toString());
            alert('Successfully unsubscribed from report emails.');
        } catch (err) {
            console.error('Failed to unsubscribe:', err);
            setError('Failed to unsubscribe from report.');
        } finally {
            setUnsubscribing(false);
        }
    };

    const handleDownload = async () => {
        if (!id) return;

        try {
            setDownloading(true);
            const response = await getReportDownloadUrl(Number(id));

            if (!response.success || !response.download_url) {
                alert(response.error || 'Failed to get download URL');
                return;
            }

            // Open the presigned URL in a new tab to trigger download
            window.open(response.download_url, '_blank');
        } catch (err) {
            console.error('Failed to download report:', err);
            alert('Failed to download report. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    const getStatusIcon = (status: GeneratedReportDetail['status']) => {
        switch (status) {
            case 'Completed':
                return <CheckCircle size={16} className="text-green-500" />;
            case 'Failed':
                return <XCircle size={16} className="text-red-500" />;
            case 'Running':
                return <Loader2 size={16} className="text-blue-500 animate-spin" />;
            case 'Pending':
                return <Clock size={16} className="text-yellow-500" />;
            default:
                return <AlertCircle size={16} className="text-gray-400" />;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                <span className="ml-2 text-sm text-gray-500">Loading report...</span>
            </div>
        );
    }

    if (error || !report) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <p className="text-red-600 mb-2">{error || 'Report not found'}</p>
                    <p className="text-sm text-gray-500">Redirecting to reports overview...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-white">
            {/* Header Toolbar */}
            <header className="flex-none bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/analytics/reports/configure')}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">{report.custom_report_name}</h1>
                        <p className="text-xs text-gray-500">{report.report_type_name} | Generated: {new Date(report.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                    </div>
                </div>

                <div className="flex gap-2">
                    {report.status === 'Completed' && (
                        <button
                            onClick={handleDownload}
                            disabled={downloading}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                        >
                            {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                            Download Excel
                        </button>
                    )}
                    <button
                        onClick={handleUnsubscribe}
                        disabled={unsubscribing}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 disabled:opacity-50"
                    >
                        {unsubscribing ? <Loader2 size={16} className="animate-spin" /> : <BellOff size={16} />}
                        Unsubscribe
                    </button>
                </div>
            </header>

            {/* Main Data Area */}
            <div className="flex-1 overflow-hidden px-6 py-6 transition-all duration-200 ease-in-out">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm h-full flex flex-col overflow-hidden">
                    {report.status !== 'Completed' ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                {getStatusIcon(report.status)}
                                <p className="mt-2 text-gray-600">
                                    {report.status === 'Running' && 'Report is being generated...'}
                                    {report.status === 'Pending' && 'Report is queued for generation...'}
                                    {report.status === 'Failed' && 'Report generation failed.'}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex-1 overflow-hidden flex flex-col">
                            <div className="flex-1 overflow-hidden h-full">
                                <DataGrid
                                    data={rows}
                                    columns={gridColumns}
                                    showToolbar={false}
                                    enableRowSelection={false}
                                    enableColumnResizing={true}
                                    enableSorting={true}
                                    showPagination={false}
                                    rowDensity="normal"
                                    fullHeight={true}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReportViewerPage;
